'use client';

import { useEffect, useState, useSyncExternalStore } from 'react';
import { Card } from '@/components/Card';
import { PageTitle } from '@/components/PageTitle';
import { ColorSchemeToggle } from '@/components/ColorSchemeToggle';
import { ThemePicker } from '@/components/ThemePicker';
import {
  getServerSnapshot,
  getSnapshot,
  listPasskeys,
  listPushDevices,
  type Passkey,
  type PushDevice,
  registerPasskey,
  revokePasskey,
  revokePushDevice,
  rotateBackupCodes,
  subscribe,
} from '@/lib/auth';

/**
 * Settings page — appearance + account controls. The API URL is baked in at
 * build time from `NEXT_PUBLIC_API_URL`, not configurable here. The auth
 * bearer token is minted by /login and lives in module memory + a refresh
 * token in localStorage (see lib/auth).
 */
export default function SettingsPage() {
  return (
    <div className="flex flex-col gap-6 max-w-2xl">
      <PageTitle>Settings</PageTitle>

      <Card title="Appearance">
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-fg-primary">Color scheme</h3>
            <p className="text-sm text-fg-muted">
              <span className="font-medium text-fg-primary">Auto</span> follows
              your operating system. Pick Light or Dark to override.
            </p>
            <ColorSchemeToggle />
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="text-sm font-medium text-fg-primary">Palette</h3>
            <p className="text-sm text-fg-muted">
              The hero palette tints the dashboard, charts, and primary
              actions.
            </p>
            <ThemePicker />
          </div>
        </div>
      </Card>

      <Card title="Account">
        <p className="text-sm text-fg-muted">
          Authentication lives under{' '}
          <code className="font-mono">/login</code>. Use the{' '}
          <span className="font-mono">Sign out</span> button in the sidebar
          to clear the session on this device.
        </p>
      </Card>

      <BackupCodesCard />
      <PasskeysCard />
      <PushDevicesCard />
    </div>
  );
}

/**
 * Visible only when 2FA is enabled — shows the count of remaining backup
 * codes and a button to rotate the set. The new codes are revealed in a
 * one-time panel; rotating again before saving them is fine because the
 * panel never goes away on its own.
 */
function BackupCodesCard() {
  const auth = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (!auth.me?.totp_enabled) return null;

  async function rotate() {
    setError(null);
    setBusy(true);
    try {
      const fresh = await rotateBackupCodes();
      setCodes(fresh);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'rotate failed');
    } finally {
      setBusy(false);
    }
  }

  function copy() {
    if (codes) void navigator.clipboard.writeText(codes.join('\n'));
  }

  return (
    <Card title="Backup codes">
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-(--color-muted)">
          {auth.me.backup_codes_remaining} unused {auth.me.backup_codes_remaining === 1 ? 'code' : 'codes'}{' '}
          remaining. Generate a fresh set when you&apos;re running low or
          suspect the old set is exposed — the previous codes stop working
          immediately.
        </p>
        {codes ? (
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2 font-mono text-base bg-(--color-card) p-3 rounded-md border border-(--color-card-border)">
              {codes.map((c) => (
                <div key={c} className="select-all tracking-tight">{c}</div>
              ))}
            </div>
            <p className="text-xs text-(--color-negative)">
              Shown once. Save them now — closing this panel won&apos;t
              re-show them.
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={copy}
                className="rounded-md border border-(--color-card-border) px-3 py-1.5 text-xs hover:bg-(--color-card)"
              >
                Copy all
              </button>
              <button
                type="button"
                onClick={() => setCodes(null)}
                className="rounded-md border border-(--color-card-border) px-3 py-1.5 text-xs hover:bg-(--color-card)"
              >
                I&apos;ve saved them
              </button>
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={rotate}
            disabled={busy}
            className="self-start rounded-md border border-(--color-card-border) px-3 py-1.5 text-sm hover:bg-(--color-card) disabled:opacity-50"
          >
            {busy ? 'Generating…' : 'Generate new backup codes'}
          </button>
        )}
        {error && (
          <p className="text-xs text-(--color-negative)">{error}</p>
        )}
      </div>
    </Card>
  );
}

/**
 * Passkey enrolment + management. Browser-driven WebAuthn ceremony — the
 * server never sees the private key, only the attestation we round-trip
 * through ``navigator.credentials.create``.
 */
function PasskeysCard() {
  const auth = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [items, setItems] = useState<Passkey[] | null>(null);
  const [nickname, setNickname] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    let cancelled = false;
    listPasskeys()
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, auth.me?.passkey_count]);

  if (auth.status !== 'authenticated') return null;

  async function enroll() {
    if (!nickname.trim()) {
      setError('Give the passkey a nickname so you can identify it later.');
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await registerPasskey(nickname.trim());
      setNickname('');
      setItems(await listPasskeys());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'enrol failed');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(id: string) {
    setError(null);
    try {
      await revokePasskey(id);
      setItems(await listPasskeys());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'revoke failed');
    }
  }

  return (
    <Card title="Passkeys">
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-(--color-muted)">
          Sign in with FaceID, TouchID, Windows Hello, or a security key. A
          passkey is bound to this device — you&apos;ll need to enrol once per
          device you want to use.
        </p>

        {items && items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {items.map((p) => (
              <li
                key={p.id}
                className="flex items-center justify-between rounded-md border border-(--color-card-border) px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{p.nickname}</span>
                  <span className="text-xs text-(--color-muted)">
                    enrolled {new Date(p.created_at).toLocaleDateString()}
                    {p.last_used_at &&
                      ` · last used ${new Date(p.last_used_at).toLocaleDateString()}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => revoke(p.id)}
                  className="rounded-md border border-(--color-card-border) px-2 py-1 text-xs hover:bg-(--color-card)"
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : items === null ? (
          <p className="text-xs text-(--color-muted)">Loading…</p>
        ) : (
          <p className="text-xs text-(--color-muted)">No passkeys enrolled yet.</p>
        )}

        <div className="flex gap-2 items-stretch">
          <input
            type="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. MacBook Pro"
            className="flex-1 rounded-md border border-(--color-card-border) bg-transparent px-3 py-1.5 text-sm"
          />
          <button
            type="button"
            onClick={enroll}
            disabled={busy}
            className="rounded-md bg-(--color-accent) px-3 py-1.5 text-white text-sm hover:opacity-90 disabled:opacity-50"
          >
            {busy ? 'Enrolling…' : 'Add a passkey'}
          </button>
        </div>

        {error && <p className="text-xs text-(--color-negative)">{error}</p>}
      </div>
    </Card>
  );
}

/**
 * Push device list. Web is read-only — enrolment happens in the Flowlet
 * iOS app where the Secure Enclave keypair is generated. Web shows the
 * list so users can revoke a stolen / lost device.
 */
function PushDevicesCard() {
  const auth = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const [items, setItems] = useState<PushDevice[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (auth.status !== 'authenticated') return;
    let cancelled = false;
    listPushDevices()
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, [auth.status, auth.me?.push_device_count]);

  if (auth.status !== 'authenticated') return null;

  async function revoke(id: string) {
    setError(null);
    try {
      await revokePushDevice(id);
      setItems(await listPushDevices());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'revoke failed');
    }
  }

  return (
    <Card title="Push devices">
      <div className="flex flex-col gap-3 text-sm">
        <p className="text-(--color-muted)">
          iOS devices that can approve sign-ins via push notification. Enrol
          one in the Flowlet iOS app under{' '}
          <span className="font-mono">Settings → Push approvals</span>.
        </p>
        {items && items.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {items.map((d) => (
              <li
                key={d.id}
                className="flex items-center justify-between rounded-md border border-(--color-card-border) px-3 py-2"
              >
                <div className="flex flex-col">
                  <span className="font-medium">{d.nickname}</span>
                  <span className="text-xs text-(--color-muted)">
                    {d.platform} · enrolled {new Date(d.created_at).toLocaleDateString()}
                    {d.last_seen_at &&
                      ` · last seen ${new Date(d.last_seen_at).toLocaleDateString()}`}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => revoke(d.id)}
                  className="rounded-md border border-(--color-card-border) px-2 py-1 text-xs hover:bg-(--color-card)"
                >
                  Revoke
                </button>
              </li>
            ))}
          </ul>
        ) : items === null ? (
          <p className="text-xs text-(--color-muted)">Loading…</p>
        ) : (
          <p className="text-xs text-(--color-muted)">No push devices enrolled.</p>
        )}
        {error && <p className="text-xs text-(--color-negative)">{error}</p>}
      </div>
    </Card>
  );
}
